import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import {
  Activity, Wifi, LogIn, RefreshCw, Loader2, Brain, Target,
  Users, TrendingUp, Volume2, VolumeX, Radio, UserPlus, Bot,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ACTION_CONFIG, playAlertSound, formatRelativeTime } from '../../components/admin/activityUtils';
import { OnlineUsersTab } from '../../components/admin/OnlineUsersTab';
import { ActivityLogTab } from '../../components/admin/ActivityLogTab';
import { UserDetailDialog } from '../../components/admin/UserDetailDialog';

const STAT_CARDS = [
  { key: 'online_now', label: 'En Linea Ahora', icon: Wifi, iconColor: 'text-green-400', bg: 'bg-green-500/20' },
  { key: 'today_logins', label: 'Logins Hoy', icon: LogIn, iconColor: 'text-blue-400', bg: 'bg-blue-500/20' },
  { key: 'today_predictions', label: 'Predicciones Hoy', icon: Brain, iconColor: 'text-purple-400', bg: 'bg-purple-500/20' },
  { key: 'today_recommendations', label: 'Recomendaciones Hoy', icon: Target, iconColor: 'text-green-400', bg: 'bg-green-500/20' },
  { key: 'unique_users_today', label: 'Usuarios Unicos Hoy', icon: Users, iconColor: 'text-amber-400', bg: 'bg-amber-500/20' },
  { key: 'week_logins', label: 'Logins Esta Semana', icon: TrendingUp, iconColor: 'text-cyan-400', bg: 'bg-cyan-500/20' },
];

const AdminUserActivity = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filterAction, setFilterAction] = useState('all');
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [liveFeed, setLiveFeed] = useState([]);
  const lastCheckRef = useRef(null);
  const isFirstLoadRef = useRef(true);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    const h = { headers: getHeaders() };
    try {
      const [statsRes, onlineRes, logRes] = await Promise.all([
        axios.get(`${API}/admin/activity/stats`, h),
        axios.get(`${API}/admin/activity/online`, h),
        axios.get(`${API}/admin/activity/log?limit=100${filterAction !== 'all' ? `&action=${filterAction}` : ''}`, h),
      ]);
      setStats(statsRes.data);
      setOnlineUsers(onlineRes.data.online_users || []);
      setActivities(logRes.data.activities || []);
      if (isFirstLoadRef.current && logRes.data.activities?.length > 0) {
        lastCheckRef.current = logRes.data.activities[0].created_at;
        isFirstLoadRef.current = false;
      }
    } catch (e) {
      toast.error('Error al cargar datos de actividad');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getHeaders, filterAction]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const pollAlerts = async () => {
      if (!lastCheckRef.current || !token) return;
      try {
        const res = await axios.get(
          `${API}/admin/activity/recent?since=${encodeURIComponent(lastCheckRef.current)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const newEvents = res.data.activities || [];
        if (newEvents.length > 0) {
          lastCheckRef.current = newEvents[newEvents.length - 1].created_at;
          setLiveFeed(prev => [...newEvents, ...prev].slice(0, 10));
          fetchAll();
          newEvents.forEach((ev) => {
            const config = ACTION_CONFIG[ev.action] || ACTION_CONFIG.login;
            if (ev.action === 'register') {
              if (soundEnabled) playAlertSound('register');
              toast.success(`Nuevo usuario registrado: ${ev.user_name || ev.user_email}`, { duration: 8000 });
            } else if (['super_ia', 'prediction'].includes(ev.action)) {
              if (soundEnabled) playAlertSound('prediction');
              toast.info(`${ev.user_name || ev.user_email} genero una prediccion`, { duration: 6000 });
            } else if (ev.action === 'login') {
              if (soundEnabled) playAlertSound('default');
              toast(`${ev.user_name || ev.user_email} inicio sesion`, { duration: 4000 });
            }
          });
        }
      } catch (e) { console.error('Polling error:', e.message); }
    };
    const interval = setInterval(pollAlerts, 10000);
    return () => clearInterval(interval);
  }, [token, soundEnabled, fetchAll]);

  const openUserDetail = async (userId) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await axios.get(`${API}/admin/activity/user/${userId}`, { headers: getHeaders() });
      setUserDetail(res.data);
    } catch (e) { toast.error('Error al cargar detalle del usuario'); }
    finally { setDetailLoading(false); }
  };

  if (loading) {
    return (
      <AdminLayout title="Monitoreo de Usuarios" subtitle="Cargando...">
        <div className="flex items-center justify-center h-64">
          <Loader2 size={40} className="text-cyan-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Monitoreo de Usuarios" subtitle="Actividad en tiempo real">
      {/* Live Feed Bar */}
      {liveFeed.length > 0 && (
        <div className="mb-6 p-3 rounded-xl bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10 border border-cyan-500/20 overflow-hidden" data-testid="live-feed-bar">
          <div className="flex items-center gap-2 mb-2">
            <Radio size={14} className="text-cyan-400 animate-pulse" />
            <span className="text-cyan-400 text-xs font-semibold uppercase tracking-wider">Feed en Vivo</span>
          </div>
          <div className="space-y-1.5">
            {liveFeed.slice(0, 5).map((ev, idx) => {
              const config = ACTION_CONFIG[ev.action] || ACTION_CONFIG.login;
              const Icon = config.icon;
              return (
                <div key={ev.id || `feed-${idx}`} className="flex items-center gap-2.5 text-sm animate-in fade-in slide-in-from-top-2 duration-300" style={{ animationDelay: `${idx * 50}ms` }}>
                  <div className={`w-6 h-6 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={12} className={config.color} />
                  </div>
                  <span className="text-white font-medium">{ev.user_name || ev.user_email}</span>
                  <span className="text-gray-500">-</span>
                  <span className={config.color}>{config.label}</span>
                  {ev.location?.city && <span className="text-gray-600 text-xs ml-auto">{ev.location.city}</span>}
                  <span className="text-gray-700 text-xs">{formatRelativeTime(ev.created_at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-green-400 text-xs font-medium">En Vivo</span>
          </div>
          <span className="text-gray-600 text-xs">Actualizacion cada 10s</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSoundEnabled(!soundEnabled)}
            className={soundEnabled ? 'text-cyan-400 hover:bg-cyan-500/10' : 'text-gray-600 hover:bg-gray-800'}
            data-testid="sound-toggle-btn">
            {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </Button>
          <Button onClick={() => { setRefreshing(true); fetchAll(); }} variant="outline" size="sm"
            className="border-gray-700 text-gray-300 hover:bg-gray-800" disabled={refreshing} data-testid="activity-refresh-btn">
            <RefreshCw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="activity-stats-grid">
        {STAT_CARDS.map(({ key, label, icon: Icon, iconColor, bg }) => (
          <Card key={key} className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <div className={`w-10 h-10 rounded-full ${bg} flex items-center justify-center mx-auto mb-2`}>
                <Icon size={20} className={iconColor} />
              </div>
              <p className="text-2xl font-bold text-white">{stats?.[key] || 0}</p>
              <p className="text-gray-500 text-xs mt-1">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="online" className="space-y-6">
        <TabsList className="bg-gray-900 border border-gray-800 p-1">
          <TabsTrigger value="online" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400" data-testid="tab-online">
            <Wifi size={16} className="mr-2" />
            En Linea ({onlineUsers.length})
          </TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400" data-testid="tab-activity">
            <Activity size={16} className="mr-2" />
            Historial
          </TabsTrigger>
        </TabsList>
        <TabsContent value="online">
          <OnlineUsersTab onlineUsers={onlineUsers} onSelectUser={openUserDetail} />
        </TabsContent>
        <TabsContent value="activity">
          <ActivityLogTab
            activities={activities}
            filterAction={filterAction}
            onFilterChange={(v) => { setFilterAction(v); setLoading(true); }}
            onSelectUser={openUserDetail}
          />
        </TabsContent>
      </Tabs>

      <UserDetailDialog open={detailOpen} onOpenChange={setDetailOpen} loading={detailLoading} userDetail={userDetail} />
    </AdminLayout>
  );
};

export default AdminUserActivity;
